import React, { useEffect } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useGetUserProfileQuery } from '@/graphql/GetUserProfile.generated';

import { setAppUser } from '@/store';

function UserProfileProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const {
    isLoading,
    user,
    error: auth0error,
    isAuthenticated,
    logout,
  } = useAuth0();

  const [{ fetching, data, error }, _reexecuteQuery] = useGetUserProfileQuery({
    pause: !isAuthenticated || !user || !user.sub,
    variables: {
      id: user?.sub as string,
    },
  });

  useEffect(() => {
    if (data) {
      setAppUser(data.container_insights_users_by_pk);
    }
  }, [data]);

  const handleClose = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  if (isLoading || fetching) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    );
  }

  if (error || auth0error) {
    return (
      <Dialog open={true}>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6">Unable to login</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Please retry or contact an administrator at{' '}
            <Link href="mailto:support@greensee.ai?subject=Seacube portal: Unable to login">
              support@greensee.ai
            </Link>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} autoFocus>
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return children;
}

export { UserProfileProvider };
